import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Clock, ChevronRight } from "lucide-react";

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const TYPE_TINT = {
  morning:    "bg-amber-50 text-amber-700 border-amber-200",
  front_desk: "bg-emerald-50 text-emerald-700 border-emerald-200",
  custom:     "bg-zinc-100 text-zinc-700 border-zinc-200",
  evening:    "bg-blue-50 text-blue-700 border-blue-200",
  night:      "bg-violet-50 text-violet-700 border-violet-200",
};

/**
 * Compact card showing the user's shifts for the next 7 days.
 * Designed to slot into the Dashboard grid. Hidden when the user has zero
 * upcoming shifts to keep the dashboard clean.
 */
export default function UpcomingShiftsCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const dateFrom = fmt(new Date());
  const dateTo = fmt(new Date(Date.now() + 6 * 24 * 60 * 60 * 1000));

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get(`/shifts?date_from=${dateFrom}&date_to=${dateTo}&user_id=${user.id}`);
      setShifts(data || []);
    } catch (_e) {
      setShifts([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i += 1) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      out.push({ date: fmt(d), label: d.toLocaleDateString([], { weekday: "short", day: "2-digit", month: "short" }) });
    }
    return out;
  }, []);

  const shiftByDate = useMemo(() => {
    const map = {};
    for (const s of shifts) map[s.date] = s;
    return map;
  }, [shifts]);

  if (loading) return null;
  if (shifts.length === 0) return null;  // nothing to show — stay out of the way

  return (
    <section className="bg-white border border-zinc-200 rounded-md p-4 sm:p-5" data-testid="upcoming-shifts-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-zinc-500" />
          <h3 className="font-display text-base sm:text-lg font-bold tracking-tight">Your upcoming shifts</h3>
        </div>
        <button
          onClick={() => navigate("/my-shifts")}
          className="text-xs font-semibold uppercase tracking-widest text-zinc-500 hover:text-zinc-900 inline-flex items-center"
          data-testid="upcoming-shifts-open"
        >
          See roster <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {days.map((d) => {
          const s = shiftByDate[d.date];
          const tint = s ? (TYPE_TINT[s.type] || TYPE_TINT.custom) : "";
          return (
            <div
              key={d.date}
              className={`rounded border p-2 ${s ? tint : "border-dashed border-zinc-200 bg-zinc-50/40"}`}
              data-testid={`upcoming-shift-${d.date}`}
            >
              <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{d.label}</div>
              {s ? (
                <>
                  <div className="font-bold text-sm capitalize truncate mt-0.5">{(s.type || "").replace(/_/g, " ")}</div>
                  <div className="text-[11px] font-mono flex items-center gap-1 mt-0.5 opacity-80">
                    <Clock className="w-3 h-3" /> {s.start_time}–{s.end_time}
                  </div>
                </>
              ) : (
                <div className="text-zinc-300 text-xs mt-1">—</div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
